import { z } from 'zod';
import { JsonValueSchema } from '../../common.schemas';
import { CardType } from '../../../samba-payment.types';
import {
    CardPointeGatewayTransactionDetailsType,
    CardPointeTransactionDetailsOutcome,
    type CardPointeInquireResponseBody,
    type CardPointeNormalizedGatewayTransactionDetails,
    type CardPointeTransactionDetailsRequestDetails,
    type CardPointeTransactionDetailsRequestPathParams,
    type CardPointeTransactionDetailsResponseDetails,
} from '../../../types/ipg/cardpointe/cardpointe-transaction-details.types';
import { CardPointeGatewayId } from '../../../types/ipg/cardpointe/cardpointe-system-constants';
import { JsonNonnegativeLosslessNumberInputSchema } from '../../common.schemas';

export const CardPointeGatewayTransactionCardTypeSchema: z.ZodType<CardType> = z.enum(CardType);

export const CardPointeTransactionDetailsRequestPathParamsSchema: z.ZodType<CardPointeTransactionDetailsRequestPathParams> =
    z.object({
        retref: z.string().min(1),
        merchid: z.string().min(1),
    });

export const CardPointeTransactionDetailsRequestDetailsSchema: z.ZodType<CardPointeTransactionDetailsRequestDetails> =
    z.object({
        method: z.literal('GET'),
        pathTemplate: z.literal('/cardconnect/rest/inquire/{retref}/{merchid}'),
        pathParams: CardPointeTransactionDetailsRequestPathParamsSchema,
    });

export const CardPointeInquireResponseBodySchema: z.ZodType<CardPointeInquireResponseBody> = z
    .object({
        amount: z.string().min(1).optional(),
        orderId: z.string().min(1).optional(),
        resptext: z.string().min(1).optional(),
        setlstat: z.string().min(1).optional(),
        respcode: z.string().min(1).optional(),
        merchid: z.string().min(1).optional(),
        token: z.string().min(1).optional(),
        respproc: z.string().min(1).optional(),
        authdate: z.string().min(1).optional(),
        lastfour: z
            .string()
            .regex(/^\d{4}$/)
            .optional(),
        name: z.string().optional(),
        expiry: z.string().min(1).optional(),
        currency: z.string().min(1).optional(),
        retref: z.string().min(1).optional(),
        respstat: z.union([z.literal('A'), z.literal('B'), z.literal('C')]).optional(),
        account: z.string().min(1).optional(),
        bintype: z.string().optional(),
        entrymode: z.string().min(1).optional(),
        authcode: z.string().min(1).optional(),
        batchid: z.string().min(1).optional(),
        emvTagData: JsonValueSchema.optional(),
        receipt: JsonValueSchema.optional(),
        receiptObj: JsonValueSchema.optional(),
        userfields: JsonValueSchema.optional(),
        voidable: z.union([z.literal('Y'), z.literal('N')]).optional(),
        refundable: z.union([z.literal('Y'), z.literal('N')]).optional(),
        declineCategory: z.string().min(1).optional(),
        declineCategoryText: z.string().min(1).optional(),
        merchAdviceCode: z.string().min(1).optional(),
        merchAdviceText: z.string().min(1).optional(),
        fee_amount: z
            .string()
            .regex(/^\d+(?:\.\d{2})?$/)
            .optional(),
        fee_value: z.string().regex(/^\d+$/).optional(),
        fee_type: z.enum(['SURCHRG', 'SURCHRG_WAIVED']).optional(),
        fee_format: z.enum(['percent', '']).optional(),
        fee_authcode: z.string().optional(),
        fee_merchid: z.string().optional(),
        fee_retref: z.string().optional(),
    })
    .catchall(JsonValueSchema);

export const CardPointeNormalizedGatewayTransactionDetailsSchema: z.ZodType<CardPointeNormalizedGatewayTransactionDetails> =
    z.object({
        gateway_id: z.literal(CardPointeGatewayId),
        gateway_transaction_details_type: z.literal(CardPointeGatewayTransactionDetailsType),
        gateway_transaction_details_version: z.literal(1),
        gateway_txn_id: z.string().min(1),
        base_amount: JsonNonnegativeLosslessNumberInputSchema,
        total_amount: JsonNonnegativeLosslessNumberInputSchema,
        total_fee_amount: JsonNonnegativeLosslessNumberInputSchema,
        total_tax_amount: JsonNonnegativeLosslessNumberInputSchema,
        is_void: z.boolean(),
        is_settled: z.boolean(),
        refund_amount: JsonNonnegativeLosslessNumberInputSchema,
        refund_count: z.number().int().nonnegative(),
        card_brand: z.string().min(1).optional(),
        card_last4: z
            .string()
            .regex(/^\d{4}$/)
            .optional(),
        cardholder_name: z.string().min(1).optional(),
        card_type: CardPointeGatewayTransactionCardTypeSchema.optional(),
        settlement_status: z.string().min(1).optional(),
        authorization_code: z.string().min(1).optional(),
        batch_id: z.string().min(1).optional(),
    });

export const CardPointeTransactionDetailsResponseDetailsSchema: z.ZodType<CardPointeTransactionDetailsResponseDetails> =
    z.discriminatedUnion('outcome', [
        z.object({
            outcome: z.literal(CardPointeTransactionDetailsOutcome.FOUND),
            httpStatus: z.literal(200),
            rawDetails: CardPointeInquireResponseBodySchema,
            normalizedDetails: CardPointeNormalizedGatewayTransactionDetailsSchema,
        }),
        z.object({
            outcome: z.literal(CardPointeTransactionDetailsOutcome.MISSING),
            httpStatus: z.number().int().positive(),
            rawDetails: z.unknown(),
            message: z.string().min(1),
        }),
        z.object({
            outcome: z.literal(CardPointeTransactionDetailsOutcome.MALFORMED_RESPONSE),
            httpStatus: z.number().int().positive(),
            rawDetails: z.unknown(),
            message: z.string().min(1),
        }),
    ]);
